import { useState } from "react";
import { ButtonBase, styled, Typography, useTheme } from "@mui/material";
import InfoIcon from "@icons/myicons/info3.svg?react";
import WarningIcon from "@icons/myicons/warning-circle.svg?react";
import { useTranslation } from "react-i18next";
import { useGetComplianceReportStatus } from "@rentablo/api/apiv1";
import { formatDateDDMMYYYY, formatDateHourly } from "@helpers/date/dateFormat";
import { differenceInDays } from "date-fns";
import { KicsOnboardingModal } from "@mandatorFeatures/kpmg/kicsOnboarding";

const STALE_THRESHOLD_DAYS = 2;

const VARIANTS = {
  info: { Icon: InfoIcon, translationKey: "kpmg.sidebar.lastUpdated" },
  warning: {
    Icon: WarningIcon,
    translationKey: "kpmg.sidebar.lastUpdatedWarning",
  },
};

const BannerButton = styled(ButtonBase)<{ variant: "info" | "warning" }>(
  ({ theme, variant }) => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(2.5),
    padding: theme.spacing(3, 5, 3, 4),
    backgroundColor: theme.palette.background[variant],
    borderRadius: 6,
    width: "100%",
    textAlign: "left",
    "&:hover": {
      filter: "brightness(0.97)",
    },
  })
);

export const KPMGMessage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data } = useGetComplianceReportStatus();
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  const reportDate = data?.lastSuccessfulReportDate
    ? new Date(data.lastSuccessfulReportDate)
    : null;

  const openGuide = () => setIsGuideOpen(true);

  let variant: "info" | "warning" = "info";
  let message: string;

  if (reportDate) {
    const days = differenceInDays(new Date(), reportDate);
    variant = days > STALE_THRESHOLD_DAYS ? "warning" : "info";
    message = t(VARIANTS[variant].translationKey, {
      days,
      date: formatDateDDMMYYYY(reportDate),
      time: formatDateHourly(reportDate.getTime()),
    });
  } else {
    message = t("kpmg.sidebar.onboardingGuideLink");
  }

  const { Icon } = VARIANTS[variant];

  return (
    <>
      <BannerButton
        variant={variant}
        onClick={openGuide}
        data-testid="kpmg-sidebar-banner"
      >
        <Icon
          color={theme.palette[variant].main}
          style={{ minHeight: "24px", minWidth: "24px" }}
        />
        <Typography
          variant="body3"
          fontWeight={reportDate ? 400 : 500}
          color={theme.palette[variant].dark}
        >
          {message}
        </Typography>
      </BannerButton>
      <KicsOnboardingModal
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
      />
    </>
  );
};
